import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import {
  ApplicationRef,
  APP_INITIALIZER,
  DoBootstrap,
  ErrorHandler,
  Inject,
  NgModule,
  TransferState,
  PLATFORM_ID,
  APP_ID,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MemoryStorage } from 'angular-oauth2-oidc';
import { NgProgressModule } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { NgProgressRouterModule } from 'ngx-progressbar/router';

import { ComponentModule } from '~/component/component.module';
import { NotificationsModule } from '~/notifications/notifications.module';

import { environment } from 'src/environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_STORAGE } from './app.token';
import { AuthModule } from './auth/auth.module';
import { CoreModule } from './core/core.module';
import { BrowserStateInterceptor } from './core/interceptors/browser-state.interceptor';
import { LoaderService } from './core/services/loader/loader.service';
import { GlobalErrorHandler } from './errors/services/error/global.handler';
import { I18nModule } from './i18n/i18n.module';
import { I18nLoader } from './i18n/services/i18n.loader';
import {
  UnleashService,
  UNLEASH_ALWAYS_ENABLE,
} from './toggles/services/unleash.service';
import { TogglesModule } from './toggles/toggles.module';
import { WidgetComponent } from './widget/widget.component';
import { WidgetModule } from './widget/widget.module';
import { AngularSvgIconModule, SvgLoader } from 'angular-svg-icon';
import { SvgServerLoader } from './utils/svg-server-loader';
import { SvgBrowserLoader } from './utils/svg-browser-loader';

// AoT requires an exported function for factories
export function HttpLoaderFactory(
  http: HttpClient,
  platformId: string
): I18nLoader {
  return new I18nLoader(http, platformId);
}

export function svgLoaderFactory(
  http: HttpClient,
  transferState: TransferState,
  platformId: string
): SvgServerLoader | SvgBrowserLoader {
  const iconsPath = 'assets/vtmn-play-icons';
  if (isPlatformServer(platformId)) {
    return new SvgServerLoader(`../browser/${iconsPath}`, transferState);
  } else {
    return new SvgBrowserLoader(http, transferState, platformId, iconsPath);
  }
}

/**
 * Base of the application
 */
@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.serviceWorker.enable,
      registrationStrategy: 'registerImmediately',
    }),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, PLATFORM_ID],
      },
    }),
    AppRoutingModule,
    I18nModule,
    NgProgressModule.withConfig({
      color: '#0082C3',
      spinner: false,
    }),
    NgProgressHttpModule,
    NgProgressRouterModule,
    AuthModule,
    TogglesModule,
    CoreModule,
    ComponentModule,
    NotificationsModule,
    WidgetModule,
    AngularSvgIconModule.forRoot({
      loader: {
        provide: SvgLoader,
        useFactory: svgLoaderFactory,
        deps: [HttpClient, TransferState, PLATFORM_ID],
      },
    }),
  ],
  providers: [
    { provide: APP_ID, useValue: 'serverApp' },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        unleashService: UnleashService,
        unleashAlwaysEnableFeatures: boolean,
        platformId: string
      ) => {
        return () => {
          if (
            isPlatformBrowser(platformId) &&
            !unleashAlwaysEnableFeatures &&
            unleashService
          ) {
            return unleashService.initialize();
          }
        };
      },
      multi: true,
      deps: [UnleashService, UNLEASH_ALWAYS_ENABLE, PLATFORM_ID],
    },
    {
      provide: APP_STORAGE,
      useFactory: (platformId: string) => {
        // local storage in browser
        if (isPlatformBrowser(platformId)) {
          return localStorage;
        }
        // simple array in memory on SSR
        return new MemoryStorage();
      },
      deps: [PLATFORM_ID],
    },
    LoaderService,
    {
      provide: 'Window',
      useFactory: (platformId: string) => {
        if (isPlatformBrowser(platformId)) {
          return window;
        }
        return {
          scrollY: 0,
        };
      },
      deps: [PLATFORM_ID],
    },
    Title,
    GlobalErrorHandler,
    {
      provide: ErrorHandler,
      useFactory: (globalErrorHandler: GlobalErrorHandler) => {
        globalErrorHandler.initialize();
        return globalErrorHandler;
      },
      deps: [GlobalErrorHandler],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BrowserStateInterceptor,
      multi: true,
    },
  ],
})
export class AppModule implements DoBootstrap {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  public ngDoBootstrap(appRef: ApplicationRef): void {
    this.bootstrapRootComponent(
      appRef,
      environment?.widget ? 'app-widget' : 'app-root'
    );
  }

  public bootstrapRootComponent(app: ApplicationRef, name: string): void {
    // define the possible bootstrap components
    // with their selectors (html host elements)
    const options = {
      'app-root': AppComponent,
      'app-widget': WidgetComponent,
    };
    // create DOM element for the component being bootstrapped
    // and add it to the DOM
    const componentElement = this.document.createElement(name);
    this.document.body.appendChild(componentElement);
    // bootstrap the application with the selected component
    const component = options[name];

    app.bootstrap(component);
  }
}
